import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const MarketingDigital = () => {
  const services = [
    {
      num: "01",
      title: "SEO / SEA",
      description: "Optimisation pour les moteurs de recherche et campagnes publicitaires ciblées.",
      features: ["Audit SEO complet", "Optimisation on-page & off-page", "Campagnes Google Ads"],
    },
    {
      num: "02",
      title: "Content Marketing",
      description: "Création de contenu stratégique pour attirer et engager votre audience.",
      features: ["Stratégie de contenu", "Rédaction d'articles de blog", "Livres blancs & études de cas"],
    },
    {
      num: "03",
      title: "Social Media",
      description: "Gestion et animation de vos réseaux sociaux pour développer votre communauté.",
      features: ["Gestion de communautés", "Création de contenu", "Publicités sociales"],
    },
    {
      num: "04",
      title: "Analytics & Reporting",
      description: "Suivi et analyse de vos performances pour des décisions data-driven.",
      features: ["Configuration GA4", "Tableaux de bord personnalisés", "Rapports mensuels"],
    },
  ];

  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end pb-20">
        <div className="absolute inset-0">
          <img
            src="/images/marketing-digital/167088.jpg"
            alt="Marketing Digital"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/70 to-gray-950/30" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6">
              Services
            </div>
            <h1 className="font-display text-display text-white mb-6">
              Marketing Digital
            </h1>
            <p className="text-xl text-gray-300 max-w-xl">
              Stratégies digitales pour augmenter votre visibilité en ligne
            </p>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-title text-white mb-6">
                Boostez Votre Présence Digitale
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Nous élaborons des stratégies marketing sur mesure pour maximiser
                votre visibilité, attirer des leads qualifiés et développer votre
                chiffre d'affaires.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                Notre approche data-driven nous permet d'optimiser continuellement
                vos campagnes pour un ROI maximal.
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/marketing-digital/marketing_digital_expertise.png"
                alt="Marketing Strategy"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Nos Services Marketing
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="border border-white/10 p-8 hover:border-flowkraft-primary/50 transition-colors"
              >
                <span className="font-display text-4xl text-flowkraft-primary/30 mb-4 block">
                  {service.num}
                </span>
                <h3 className="font-display text-2xl text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-400 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-300 text-sm">
                      <Check className="w-4 h-4 text-flowkraft-secondary mr-3" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-px bg-white/10">
            {[
              { value: "+150%", label: "Trafic moyen généré" },
              { value: "+80%", label: "Leads qualifiés" },
              { value: "4.5x", label: "ROI moyen" },
              { value: "100+", label: "Campagnes réussies" },
            ].map((stat, index) => (
              <div key={index} className="bg-gray-950 p-8 text-center">
                <div className="font-display text-4xl text-flowkraft-secondary mb-2">
                  {stat.value}
                </div>
                <p className="text-gray-500 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/main/cta_background_digital.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <h2 className="font-display text-title text-white mb-6">
            Développez votre visibilité en ligne
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Discutons de votre stratégie marketing digitale
          </p>
          <Link
            to="/#contact"
            className="btn-solid inline-flex"
          >
            Demander une stratégie
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarketingDigital;
